import { useEffect, useRef, useState } from 'react';
import { integrationTools } from '@/mocks/homeData';

export default function IntegrationSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="integration"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-firmai-dark overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-firmai-darker via-firmai-dark to-firmai-darker" />

      {/* Animated particles */}
      {isVisible && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-[20%] w-1.5 h-1.5 bg-firmai-green/40 rounded-full animate-float-data" />
          <div className="absolute top-1/2 left-[30%] w-1 h-1 bg-firmai-blue/40 rounded-full animate-float-data" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-2/3 left-[25%] w-1.5 h-1.5 bg-firmai-green/30 rounded-full animate-float-data" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/3 right-[20%] w-1 h-1 bg-firmai-blue/30 rounded-full animate-float-data-reverse" />
          <div className="absolute top-1/2 right-[30%] w-1.5 h-1.5 bg-firmai-green/40 rounded-full animate-float-data-reverse" style={{ animationDelay: '0.7s' }} />
        </div>
      )}

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 text-center mb-12 md:mb-16">
          <h2 className="font-display font-bold text-2xl md:text-4xl text-white">
            无缝集成 企业生态
          </h2>
          <p className="text-firmai-text mt-4 max-w-2xl mx-auto text-sm md:text-base">
            通过钉钉、飞书等企业工具，随时随地接收能源报告和智能提醒
          </p>
        </div>

        {/* Integration Card */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 delay-100 max-w-4xl mx-auto">
          <div className="bg-firmai-card border border-firmai-border rounded-xl p-6 md:p-10 relative overflow-hidden group hover:border-firmai-green/20 transition-all duration-500">
            {/* Glow border effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-firmai-green/10 via-transparent to-firmai-blue/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              {/* Left - DingTalk */}
              <div className="flex-1 text-center space-y-3 group/ding">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-blue-500/20 rounded-xl border border-blue-500/30 group-hover/ding:bg-blue-500/30 group-hover/ding:border-blue-500/50 transition-all duration-300">
                  <span className="w-8 h-8 flex items-center justify-center text-blue-400 group-hover/ding:scale-110 transition-transform duration-300">
                    <i className="ri-message-3-line text-2xl" />
                  </span>
                </div>
                <h4 className="text-white font-medium">钉钉</h4>
                <p className="text-firmai-muted text-xs">能源报告自动推送</p>
              </div>

              {/* Center - Connection with animated flow */}
              <div className="flex flex-col items-center gap-2 relative">
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-firmai-green/20 to-firmai-blue/20 rounded-full border border-firmai-green/30 group-hover:from-firmai-green/30 group-hover:to-firmai-blue/30 transition-all duration-300">
                  <span className="w-10 h-10 flex items-center justify-center text-firmai-green group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-link text-2xl" />
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-px bg-gradient-to-r from-transparent to-firmai-green/40 relative overflow-hidden">
                    <div className="absolute inset-0 bg-firmai-green/60 animate-flow-left" />
                  </div>
                  <span className="text-firmai-green text-xs font-medium">数据同步</span>
                  <div className="w-8 h-px bg-gradient-to-l from-transparent to-firmai-green/40 relative overflow-hidden">
                    <div className="absolute inset-0 bg-firmai-green/60 animate-flow-right" />
                  </div>
                </div>
              </div>

              {/* Right - Feishu */}
              <div className="flex-1 text-center space-y-3 group/feishu">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-emerald-500/20 rounded-xl border border-emerald-500/30 group-hover/feishu:bg-emerald-500/30 group-hover/feishu:border-emerald-500/50 transition-all duration-300">
                  <span className="w-8 h-8 flex items-center justify-center text-emerald-400 group-hover/feishu:scale-110 transition-transform duration-300">
                    <i className="ri-chat-1-line text-2xl" />
                  </span>
                </div>
                <h4 className="text-white font-medium">飞书</h4>
                <p className="text-firmai-muted text-xs">AI分析智能提醒</p>
              </div>
            </div>

            {/* Bottom - More Tools */}
            <div className="relative z-10 mt-8 pt-6 border-t border-firmai-border">
              <p className="text-firmai-muted text-xs text-center mb-4">同时支持</p>
              <div className="flex items-center justify-center gap-6 md:gap-8">
                {integrationTools.map((tool) => (
                  <div key={tool.name} className="flex flex-col items-center gap-1.5 group/tool">
                    <span className="w-8 h-8 flex items-center justify-center text-firmai-muted group-hover/tool:text-firmai-text group-hover/tool:scale-110 transition-all duration-300">
                      <i className={`${tool.icon} text-lg`} />
                    </span>
                    <span className="text-firmai-muted text-xs group-hover/tool:text-firmai-text transition-colors duration-300">
                      {tool.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .reveal.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        @keyframes float-data {
          0% { transform: translateX(0) translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(120px) translateY(-20px); opacity: 0; }
        }
        .animate-float-data {
          animation: float-data 4s ease-in-out infinite;
        }
        @keyframes float-data-reverse {
          0% { transform: translateX(0) translateY(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(-120px) translateY(20px); opacity: 0; }
        }
        .animate-float-data-reverse {
          animation: float-data-reverse 4s ease-in-out infinite;
        }
        @keyframes flow-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-flow-left {
          animation: flow-left 2s linear infinite;
        }
        @keyframes flow-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-flow-right {
          animation: flow-right 2s linear infinite;
        }
      `}</style>
    </section>
  );
}